import {
    InteractionRequiredAuthError,
    type AccountInfo,
    type PublicClientApplication,
} from "@azure/msal-browser";
import type { AppConfig } from "./authConfig";

/**
 * All BFF routes are mounted behind this prefix in dev (the Vite dev
 * server's proxy rewrites it before forwarding to :5000) and QA/prod
 * (Traefik strip-prefix middleware). The frontend therefore hits the same
 * absolute path in every environment.
 */
export const API_PREFIX = "/api/office";

/**
 * Thrown when silent token acquisition fails because the user must interact
 * (consent prompt, expired refresh token, MFA step-up, etc.).
 *
 * Callers can catch this and trigger `instance.acquireTokenRedirect(...)`.
 * Redirect is NOT triggered here — that would be a hidden navigation
 * side-effect inside what looks like a fetch call. The shell raises a
 * dedicated re-auth gate when this surfaces.
 */
export class InteractionRequiredError extends Error {
    public override readonly name: string = "InteractionRequiredError";
    public override readonly cause: unknown;

    constructor(cause: unknown) {
        super(
            cause instanceof Error
                ? `Interaction required to acquire token: ${cause.message}`
                : "Interaction required to acquire token.",
        );
        this.cause = cause;
    }
}

/**
 * Thrown when the API responds with a non-2xx status. Carries enough
 * context for the calling composition to surface a useful error without
 * re-parsing the response.
 */
export class ApiResponseError extends Error {
    public override readonly name: string = "ApiResponseError";
    public readonly status: number;
    public readonly statusText: string;
    public readonly body: string;

    constructor(status: number, statusText: string, body: string) {
        super(`API request failed: ${status} ${statusText}`);
        this.status = status;
        this.statusText = statusText;
        this.body = body;
    }
}

export interface ApiClient {
    fetch(path: string, init?: RequestInit): Promise<Response>;
    getJson<T>(path: string, init?: RequestInit): Promise<T>;
}

interface CreateApiClientDeps {
    readonly instance: PublicClientApplication;
    readonly config: AppConfig;
}

/**
 * Build a fetch wrapper that (1) prefixes every call with the BFF path,
 * (2) acquires an access token silently for `apiScope`, and (3) attaches
 * it as a `Bearer` header.
 *
 * The returned client is stateless; it reads the active account on every
 * call so it transparently follows account switches.
 */
export function createApiClient(deps: CreateApiClientDeps): ApiClient {
    const { instance, config } = deps;

    async function authenticatedFetch(path: string, init?: RequestInit): Promise<Response> {
        if (!path.startsWith("/")) {
            throw new Error(
                `apiClient.fetch: path must start with '/', received: ${JSON.stringify(path)}`,
            );
        }

        const account: AccountInfo | null =
            instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
        if (account === null) {
            throw new InteractionRequiredError(
                new Error("apiClient.fetch: no active account — user is not signed in."),
            );
        }

        let accessToken: string;
        try {
            const result = await instance.acquireTokenSilent({
                scopes: [config.msal.apiScope],
                account,
            });
            accessToken = result.accessToken;
        } catch (err) {
            if (err instanceof InteractionRequiredAuthError) {
                throw new InteractionRequiredError(err);
            }
            throw err;
        }

        const headers = new Headers(init?.headers);
        if (!headers.has("Accept")) {
            headers.set("Accept", "application/json");
        }
        headers.set("Authorization", `Bearer ${accessToken}`);

        const url = `${API_PREFIX}${path}`;
        return fetch(url, { ...init, headers });
    }

    async function getJson<T>(path: string, init?: RequestInit): Promise<T> {
        const response = await authenticatedFetch(path, init);
        if (!response.ok) {
            const body = await response.text();
            throw new ApiResponseError(response.status, response.statusText, body);
        }
        return (await response.json()) as T;
    }

    return {
        fetch: authenticatedFetch,
        getJson,
    };
}
